import api from '../../utils/api';
import type {
  ActiveCallItem,
  CommunicationsActivityData,
  CommunicationsDashboardData,
  CommunicationsSettings,
  CommunicationsWebhookLog,
} from '../../models/MasterAdmin/communications';

const BASE = '/master-admin/communications';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

function extractApiError(err: unknown, fallback: string): Error {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as { response?: { data?: { message?: string } } };
    const msg = axiosErr.response?.data?.message;
    if (msg) return new Error(msg);
  }
  if (err instanceof Error && err.message) return err;
  return new Error(fallback);
}

export async function getCommunicationsSettings(): Promise<CommunicationsSettings> {
  const response = await api.get<ApiResponse<CommunicationsSettings>>(`${BASE}/settings`);
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to load communications settings');
  }
  return response.data.data;
}

export async function updateCommunicationsSettings(
  data: Partial<CommunicationsSettings> & { apiToken?: string }
): Promise<CommunicationsSettings> {
  const response = await api.put<ApiResponse<CommunicationsSettings>>(`${BASE}/settings`, data);
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to update communications settings');
  }
  return response.data.data;
}

export async function testCommunicationsCredentials(payload?: {
  apiKey?: string;
  apiToken?: string;
  accountSid?: string;
  apiSubdomain?: string;
}): Promise<void> {
  try {
    const response = await api.post<ApiResponse<unknown>>(`${BASE}/credentials/test`, payload || {});
    if (!response.data.success) {
      throw new Error(response.data.message || 'Credential test failed');
    }
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg = axiosErr.response?.data?.message;
      if (msg) throw new Error(msg);
    }
    throw err instanceof Error ? err : new Error('Credential test failed');
  }
}

export async function getCommunicationsDashboard(days = 30): Promise<CommunicationsDashboardData> {
  const response = await api.get<ApiResponse<CommunicationsDashboardData>>(`${BASE}/dashboard`, {
    params: { days },
  });
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to load communications dashboard');
  }
  return response.data.data;
}

export async function getCommunicationsActivity(days = 30): Promise<CommunicationsActivityData> {
  const response = await api.get<ApiResponse<CommunicationsActivityData>>(`${BASE}/activity`, {
    params: { days },
  });
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to load call activity');
  }
  return response.data.data;
}

export async function getCommunicationsWebhookLogs(
  days = 30,
  limit = 100
): Promise<CommunicationsWebhookLog[]> {
  const response = await api.get<ApiResponse<{ rows: CommunicationsWebhookLog[] }>>(
    `${BASE}/webhooks/logs`,
    { params: { days, limit } }
  );
  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.message || 'Failed to load webhook logs');
  }
  return response.data.data.rows;
}

export async function connectCommunicationsCall(payload: {
  from: string;
  to: string;
  record?: boolean;
  timeOut?: number;
}): Promise<{ callSid: string | null; status: string }> {
  try {
    const response = await api.post<
      ApiResponse<{ callSid: string | null; status: string }>
    >(`${BASE}/calls/connect`, payload);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to initiate call');
    }
    return response.data.data;
  } catch (err: unknown) {
    throw extractApiError(err, 'Failed to initiate call');
  }
}

export async function getActiveCommunicationsCalls(): Promise<{
  active_calls?: ActiveCallItem[];
  total_active?: number;
}> {
  const response = await api.get<
    ApiResponse<{
      response?: { active_calls?: ActiveCallItem[]; total_active?: number };
    }>
  >(`${BASE}/calls/active`);
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to load active calls');
  }
  const payload = response.data.data;
  return payload?.response || { active_calls: [], total_active: 0 };
}

export async function getCommunicationsCallDetails(callSid: string): Promise<unknown> {
  const response = await api.get<ApiResponse<unknown>>(`${BASE}/calls/${callSid}`);
  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to load call details');
  }
  return response.data.data;
}
